import { useRef, useEffect } from "react";

/**
 * Creates refs for a scroll container and trigger element that can be used to
 * load more content when the trigger enters the observer area.
 *
 * @param options.allowGetter - Enables calls to `getter` while the trigger is visible.
 * @param options.observerAreaSize - Intersection observer `rootMargin` value
 * used to expand or shrink the area that triggers `getter`.
 * @param options.getter - Callback invoked when the trigger element intersects
 * the observer area.
 * @returns Refs for the scroll container and the trigger element. Attach
 * `containerRef` to the scrolling list wrapper and `triggerRef` to an element at
 * the end of the list.
 */
export function useInfiniteScroll(options: {
  allowGetter: boolean;
  observerAreaSize: string;
  getter: () => void;
}) {
  const { allowGetter, observerAreaSize, getter } = options;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!containerRef.current || !triggerRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // If the triggerRef is in view and allowGetter is true
        if (entry.isIntersecting && allowGetter) {
          getter();
        }
      },
      {
        rootMargin: observerAreaSize,
      }
    );

    observer.observe(triggerRef.current);

    return () => observer.disconnect();
  }, [allowGetter, observerAreaSize, getter]);

  return { containerRef, triggerRef };
}
